import React, { useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { normalizeChoices } from '../utils/helpers';
import styles from './fields.module.css';

export interface RankingFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

interface RankingChoice {
  value: string;
  text: string;
}

export const RankingField: React.FC<RankingFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = errors[element.name];

  const initialChoices: RankingChoice[] = normalizeChoices(element.choices);

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={initialChoices.map((c) => c.value)}
        render={({ field }) => {
          const [items, setItems] = useState<RankingChoice[]>(initialChoices);
          const [dragIndex, setDragIndex] = useState<number | null>(null);

          const handleDragStart = (index: number) => {
            setDragIndex(index);
          };

          const handleDragOver = (e: React.DragEvent, index: number) => {
            e.preventDefault();
            if (dragIndex === null || dragIndex === index) return;

            const newItems = [...items];
            const draggedItem = newItems[dragIndex];
            newItems.splice(dragIndex, 1);
            newItems.splice(index, 0, draggedItem);

            setItems(newItems);
            setDragIndex(index);
            field.onChange(newItems.map((item) => item.value));
          };

          const handleDragEnd = () => {
            setDragIndex(null);
          };

          const moveItem = (fromIndex: number, direction: 'up' | 'down') => {
            const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
            if (toIndex < 0 || toIndex >= items.length) return;

            const newItems = [...items];
            const temp = newItems[fromIndex];
            newItems[fromIndex] = newItems[toIndex];
            newItems[toIndex] = temp;

            setItems(newItems);
            field.onChange(newItems.map((item) => item.value));
          };

          return (
            <ul className={styles.rankingList}>
              {items.map((item, index) => (
                <li
                  key={item.value}
                  className={styles.rankingItem}
                  draggable={!element.readOnly}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  style={{
                    opacity: dragIndex === index ? 0.5 : 1,
                  }}
                >
                  <span className={styles.rankingHandle}>☰</span>
                  <span style={{ flex: 1 }}>
                    {index + 1}. {item.text}
                  </span>
                  {!element.readOnly && (
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        type="button"
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0}
                        style={{
                          padding: '0.25rem 0.5rem',
                          border: '1px solid #ccc',
                          background: 'white',
                          borderRadius: '4px',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          opacity: index === 0 ? 0.5 : 1,
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === items.length - 1}
                        style={{
                          padding: '0.25rem 0.5rem',
                          border: '1px solid #ccc',
                          background: 'white',
                          borderRadius: '4px',
                          cursor:
                            index === items.length - 1
                              ? 'not-allowed'
                              : 'pointer',
                          opacity: index === items.length - 1 ? 0.5 : 1,
                        }}
                      >
                        ↓
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          );
        }}
      />
      {error && (
        <p className={styles.errorMessage}>{error.message as string}</p>
      )}
    </div>
  );
};

export default RankingField;
